import { Pipe, PipeTransform } from '@angular/core';

/**
 * GP-CB-05: one fixed deadline format everywhere ("November 1, 2026").
 * Deadlines are stored as text, so this reads every format found in the data (text dates, Excel day numbers,
 * corrupted "+046034-12-31T18:30:00.000Z" values, UTC ISO dates, dd-MM-yyyy) and shows them the same way.
 * Text that is not a date (e.g. "Rolling Admissions") is shown unchanged.
 */

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const MONTH_INDEX: { [key: string]: number } = MONTHS.reduce((map, name, i) => {
  map[name.toLowerCase()] = i;
  map[name.substring(0, 3).toLowerCase()] = i;
  return map;
}, { sept: 8 } as { [key: string]: number });

/** Builds a local date only if day/month/year are a real calendar date. */
function buildDate(year: number, monthIndex: number, day: number): Date | null {
  const d = new Date(year, monthIndex, day);
  return d.getFullYear() === year && d.getMonth() === monthIndex && d.getDate() === day ? d : null;
}

/** Excel stores dates as a day count from 30 Dec 1899. */
function fromExcelSerial(serial: number): Date | null {
  if (serial < 20000 || serial > 80000) return null;
  const utc = new Date(Date.UTC(1899, 11, 30) + serial * 86400000);
  return buildDate(utc.getUTCFullYear(), utc.getUTCMonth(), utc.getUTCDate());
}

/** Parses any deadline format found in the data. Returns null for text that is not a date. */
export function parseDeadline(value: string | null | undefined): Date | null {
  if (value === null || value === undefined) return null;
  const v = String(value).trim();
  if (!v) return null;
  let m: RegExpMatchArray | null;

  // "+046034-12-31T18:30:00.000Z": an Excel day number that a browser read as a year and saved as a date.
  if ((m = v.match(/^\+0?(\d{5})-(\d{2})-(\d{2})T/))) {
    return fromExcelSerial(Number(m[1]) + (m[2] === '12' && m[3] === '31' ? 1 : 0));
  }
  // Excel day number, e.g. "46035".
  if (/^\d{5}(\.\d+)?$/.test(v)) {
    return fromExcelSerial(Math.floor(Number(v)));
  }
  // ISO date with time, e.g. "2025-10-31T18:30:00.000Z": read in the viewer's local time.
  if (/^\d{4}-\d{2}-\d{2}T/.test(v)) {
    const d = new Date(v);
    return isNaN(d.getTime()) ? null : buildDate(d.getFullYear(), d.getMonth(), d.getDate());
  }
  // "2026-03-31"
  if ((m = v.match(/^(\d{4})-(\d{1,2})-(\d{1,2})$/))) {
    return buildDate(+m[1], +m[2] - 1, +m[3]);
  }
  // Day first: "31-03-2026", "31/03/2026", "31.03.2026", optionally followed by a time.
  if ((m = v.match(/^(\d{1,2})[-\/.](\d{1,2})[-\/.](\d{4})(\s.*)?$/))) {
    return buildDate(+m[3], +m[2] - 1, +m[1]);
  }
  const text = v.replace(/(\d{1,2})(st|nd|rd|th)\b/gi, '$1').replace(/\s+/g, ' ');
  // "November 1, 2026", "Nov 1 2026"
  if ((m = text.match(/^([A-Za-z]{3,9})\.? (\d{1,2}),? (\d{4})$/))) {
    const month = MONTH_INDEX[m[1].toLowerCase()];
    return month === undefined ? null : buildDate(+m[3], month, +m[2]);
  }
  // "1 November 2026", "15 Jan, 2025"
  if ((m = text.match(/^(\d{1,2}) ([A-Za-z]{3,9})\.?,? (\d{4})$/))) {
    const month = MONTH_INDEX[m[2].toLowerCase()];
    return month === undefined ? null : buildDate(+m[3], month, +m[1]);
  }
  return null;
}

/** Formats a date as "November 1, 2026". */
export function formatDeadline(date: Date): string {
  return `${MONTHS[date.getMonth()]} ${date.getDate()}, ${date.getFullYear()}`;
}

@Pipe({
  name: 'deadline',
  standalone: true
})
export class DeadlinePipe implements PipeTransform {
  /** {{ value | deadline }} or {{ value | deadline:'N/A' }} (shown when the value is empty). */
  transform(value: string | null | undefined, emptyText: string = ''): string {
    if (value === null || value === undefined || String(value).trim() === '') return emptyText;
    const date = parseDeadline(value);
    return date ? formatDeadline(date) : String(value).trim();
  }
}